import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, Stack } from '@mui/material';

import { InfoField, InfoFieldChangeHandler } from '../../../../../../../components';

import { UTCToDateTime } from '../../../../../../../utils/dates';
import { useGetCompanyTypesQuery, useGetCountriesQuery } from '../../../../../../../api';
import type { ProfileWithInfo } from '../../../../../../../types';

type LegalDetailInfoProps = {
  profile: ProfileWithInfo | undefined;
  isEditMode: boolean;
  fields: Partial<ProfileWithInfo> | null;
  fieldsHandler: InfoFieldChangeHandler;
  isLoading?: boolean;
};

export const LegalDetailInfo: FC<LegalDetailInfoProps> = (props) => {
  const { isEditMode, profile, fields, fieldsHandler, isLoading } = props;
  const { t } = useTranslation();

  // api
  const { data: countries } = useGetCountriesQuery();
  const { data: companyTypes } = useGetCompanyTypesQuery();

  // elements
  const countrySelectOptions = countries?.countries.map(({ alpha2code, name }) => {
    return (
      <MenuItem key={alpha2code} value={alpha2code}>
        {name}
      </MenuItem>
    );
  });

  const companyTypeSelectOptions = companyTypes?.types.map(({ id, name }) => {
    return (
      <MenuItem key={id} value={id}>
        {name}
      </MenuItem>
    );
  });

  return (
    <Stack direction="column" spacing={1}>
      <InfoField
        name={t('profile-details.detail.legal.company-name')}
        value={profile?.info?.company_name}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'company_name',
          value: fields?.info?.company_name ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.legal.registration-number')}
        value={profile?.info?.registration_number}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'registration_number',
          value: fields?.info?.registration_number ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.legal.registration-country')}
        value={profile?.info?.registration_country_code}
        formattedValue={
          profile?.info?.registration_country_code
            ? countries?.countries.find(
                (c) => c.alpha2code === profile.info?.registration_country_code
              )!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'registration_country_code',
          value: fields?.info?.registration_country_code ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: countrySelectOptions,
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.legal.registration-date')}
        value={profile?.info?.registration_date}
        formattedValue={
          profile?.info?.registration_date
            ? UTCToDateTime(profile.info.registration_date as string)
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'registration_date',
          value: fields?.info?.registration_date ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
      />

      <InfoField
        name={t('profile-details.detail.legal.additional-business-names')}
        value={profile?.info?.additional_business_names}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'additional_business_names',
          value: fields?.info?.additional_business_names ?? '',
          onChange: fieldsHandler,
          componentType: 'chip',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.formation-date')}
        value={profile?.info?.formation_date}
        formattedValue={
          profile?.info?.formation_date
            ? UTCToDateTime(profile.info.formation_date as string)
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'formation_date',
          value: fields?.info?.formation_date ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.licence-valid-to')}
        value={profile?.info?.license_valid_to}
        formattedValue={
          profile?.info?.license_valid_to
            ? UTCToDateTime(profile.info.license_valid_to as string)
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'license_valid_to',
          value: fields?.info?.license_valid_to ?? '',
          onChange: fieldsHandler,
          componentType: 'date',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.phone')}
        value={profile?.info?.phone}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'phone',
          value: fields?.info?.phone ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.email')}
        value={profile?.info?.email}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'email',
          value: fields?.info?.email ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.website')}
        value={profile?.info?.website}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'website',
          value: fields?.info?.website ?? '',
          onChange: fieldsHandler,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.company-type')}
        value={profile?.info?.company_type_id}
        formattedValue={
          profile?.info?.company_type_id
            ? companyTypes?.types.find((t) => t.id === profile.info?.company_type_id)!.name
            : undefined
        }
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'company_type_id',
          value: fields?.info?.company_type_id ?? '',
          onChange: fieldsHandler,
          componentType: 'select',
          selectOptions: companyTypeSelectOptions,
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.purpose')}
        value={profile?.info?.purpose}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'purpose',
          value: fields?.info?.purpose ?? '',
          onChange: fieldsHandler,
          componentType: 'textarea',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.description')}
        value={profile?.info?.description}
        editProps={{ withEditMode: true, isEditMode }}
        inputProps={{
          name: 'description',
          value: fields?.info?.description ?? '',
          onChange: fieldsHandler,
          componentType: 'textarea',
        }}
        isLoading={isLoading}
        withEmptyValue
      />

      <InfoField
        name={t('profile-details.detail.legal.last-modified')}
        value={profile?.info?.row_start}
        formattedValue={
          profile?.info?.row_start ? UTCToDateTime(profile.info?.row_start as string) : undefined
        }
        withoutDivider
      />
    </Stack>
  );
};
